<script setup lang="ts">
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useAccessStore } from '@/stores/access'
import {computed, h, ref} from 'vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'

import {ColumnDef} from "@tanstack/vue-table"
import {ArrowUpDown, RotateCcw, Trash2} from "lucide-vue-next"
import {Button} from '@/components/ui/button'
import {DataTable} from '@/components/ui/data-table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {Org} from '@/types'
import {formatTime} from '@/utils/datetime'

const orgStore = useOrgStore()

const userStore = useUserStore()

const accessStore = useAccessStore()
const settings = computed(() => userStore.settings)

interface OrgInvite {
  id: string
  role_id: number
  email: string
  fullname: string
  member_id: number
  is_accepted: boolean
  created_at: string
  updated_at: string
  organization_id: number
}

const props = defineProps<{
  org: Org,
  invites: OrgInvite[]
}>()

const confirmDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingDeleteId = ref<string | null>(null)

const resend = (inviteId: string) => {
  orgStore.resendOrgInvite({
    orgId: props.org.organization_id,
    inviteId
  })
}

const deleteInvite = (inviteId: string) => {
  pendingDeleteId.value = inviteId
  confirmDialog.value?.open()
}

const confirmDelete = () => {
  if (!pendingDeleteId.value) return
  orgStore.deleteOrgInvite({
    orgId: props.org.organization_id,
    inviteId: pendingDeleteId.value
  })
  pendingDeleteId.value = null
}

const columns: ColumnDef<OrgInvite>[] = [
  {
    accessorKey: 'fullname',
    header: ({column}) => {
      return h(Button, {
        variant: 'ghost',
        size: 'sm',
        onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
      }, () => ['Name', h(ArrowUpDown, {class: 'ml-1 h-3 w-3'})])
    },
    cell: ({row}) => {
      return h('div', {class: 'text-gray-900'}, row.getValue('fullname'))
    },
  },
  {
    accessorKey: 'email',
    header: ({column}) => {
      return h(Button, {
        variant: 'ghost',
        size: 'sm',
        onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
      }, () => ['Email', h(ArrowUpDown, {class: 'ml-1 h-3 w-3'})])
    },
    cell: ({row}) => {
      const invite = row.original
      return h('div', {class: 'flex items-center gap-2'}, [
        h('span', {class: 'lowercase whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px]', title: row.getValue('email')}, row.getValue('email')),
        h(TooltipProvider, {}, () =>
          h(Tooltip, {}, () => [
            h(TooltipTrigger, { asChild: true }, () =>
              h(Button, {
                variant: 'ghost',
                size: 'sm',
                onClick: () => resend(invite.id),
              }, () => h(RotateCcw, { class: 'h-3 w-3' }))
            ),
            h(TooltipContent, {}, () => 'Resend invite email'),
          ])
        )
      ])
    },
  },
  {
    id: 'role',
    header: 'Role',
    cell: ({row}) => {
      return h('div', {class: 'text-gray-500'}, accessStore.role(row.original.role_id)?.name ?? '')
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Invited',
    cell: ({row}) => {
      const date = formatTime(row.getValue('created_at'), settings.value.tz, 'D MMMM YYYY')
      return h('div', {class: 'text-gray-500 whitespace-nowrap'}, date)
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({row}) => {
      const invite = row.original
      return h(Button, {
        variant: 'ghost',
        size: 'sm',
        onClick: () => deleteInvite(invite.id),
      }, () => h(Trash2, {class: 'h-4 w-4 text-red-400'}))
    },
  }
]
</script>

<template>
  <DataTable :columns="columns" :data="invites" />
  <DialogConfirm
    ref="confirmDialog"
    title="Delete invite"
    description="Are you sure you want to delete this pending invite?"
    confirmText="Delete"
    variant="error"
    @confirm="confirmDelete"
  />
</template>
